/* Plantica — Press
   The marquee reuses the Swiper that Sleek already ships (window.FoxTheme.Swiper
   from vendor.js, loaded globally). The FreeMode module is NOT exposed in that
   bundle, so continuous motion comes from Autoplay with delay 0 plus a linear
   transition timing function in CSS: each transition starts the moment the
   previous one ends.

   Without JS or without Swiper the markup stays a readable row of cards that
   can be scrolled on narrow screens — degraded, not lost. */
(() => {
  if (customElements.get('plantica-press')) return;

  const MARQUEE_CLASS = 'is-marquee';
  const DESKTOP_QUERY = '(min-width: 990px)';
  // Milliseconds per logo when the section sends none; the section's
  // „Viteză derulare" (seconds per logo) comes in as data-speed.
  const DEFAULT_SPEED = 3500;

  class PlanticaPress extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.plantica-press__viewport');
      if (!this.viewport) return;

      this.slideCount = this.querySelectorAll('.swiper-slide').length;
      if (!this.slideCount) return;

      this.staticMax = parseInt(this.dataset.staticMax, 10) || 5;
      this.speed = parseInt(this.dataset.speed, 10) || DEFAULT_SPEED;
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.sync = this.sync.bind(this);
      this.desktop.addEventListener('change', this.sync);

      // Pausing under the pointer, asked for on 24.09: the cards are links and
      // carry a quote, and neither can be used while they glide past.
      this.freeze = this.freeze.bind(this);
      this.thaw = this.thaw.bind(this);
      this.addEventListener('mouseenter', this.freeze);
      this.addEventListener('mouseleave', this.thaw);
      this.addEventListener('focusin', this.freeze);
      this.addEventListener('focusout', this.thaw);

      this.sync();
    }

    disconnectedCallback() {
      if (this.desktop) this.desktop.removeEventListener('change', this.sync);
      this.stop();
    }

    // Marquee always runs on mobile, and on desktop only when there are more
    // cards than fit comfortably in a single row.
    shouldRun() {
      if (this.slideCount < 2) return false;
      return !this.desktop.matches || this.slideCount > this.staticMax;
    }

    sync() {
      // Rebuild on breakpoint change: spaceBetween and drag differ per device.
      this.stop();
      if (this.shouldRun()) this.start();
    }

    start() {
      if (this.swiper) return;

      const api = window.FoxTheme && window.FoxTheme.Swiper;
      if (!api || !api.Swiper) return;

      const { Swiper, Autoplay } = api;
      const animate = !this.reduced.matches;
      const isDesktop = this.desktop.matches;

      this.classList.add(MARQUEE_CLASS);

      this.swiper = new Swiper(this.viewport, {
        modules: animate ? [Autoplay] : [],
        slidesPerView: 'auto',
        spaceBetween: isDesktop ? 20 : 10,
        loop: true,
        loopAdditionalSlides: this.slideCount,
        speed: this.speed,
        // Drag is mobile only — on desktop the strip just runs.
        allowTouchMove: !isDesktop,
        grabCursor: !isDesktop,
        // Swiper's own `pauseOnMouseEnter` is no use here: with `delay: 0` the
        // pause happens between transitions, so the strip keeps gliding for up
        // to a whole slide after the pointer arrives. freeze()/thaw() below stop
        // it where it stands instead.
        autoplay: animate
          ? { delay: 0, disableOnInteraction: false }
          : false,
      });
    }

    stop() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.paused = false;
      this.classList.remove(MARQUEE_CLASS);
    }

    /* Where the track actually is right now — read off the element, not from
       Swiper, which already holds the end of the running transition. */
    translate() {
      const matrix = new DOMMatrixReadOnly(getComputedStyle(this.swiper.wrapperEl).transform);
      return matrix.m41;
    }

    /* One card plus its gap: the distance a slide covers in this.speed. */
    step() {
      const snaps = this.swiper.snapGrid || [];
      return snaps.length > 1 ? snaps[1] - snaps[0] : 0;
    }

    freeze() {
      const swiper = this.swiper;
      if (!swiper || !swiper.autoplay || this.paused) return;

      const at = this.translate();
      this.paused = true;
      swiper.autoplay.stop();
      swiper.setTransition(0);
      swiper.setTranslate(at);

      // ⚠️ The transition we just cut short will never report back, and Swiper
      // refuses to start another move while it believes one is running. Without
      // this line the strip freezes and never leaves — measured, not guessed.
      swiper.animating = false;

      if (this.resumed) {
        swiper.off('transitionEnd', this.resumed);
        this.resumed = null;
      }
    }

    thaw() {
      const swiper = this.swiper;
      if (!swiper || !swiper.autoplay || !this.paused) return;
      this.paused = false;

      const from = -swiper.translate;
      const next = (swiper.snapGrid || []).find((snap) => snap > from + 1);
      const step = this.step();

      // Handing it back to autoplay alone would give what is left of a card the
      // time a whole one gets, and the strip would crawl out of the pause. The
      // remaining distance gets the matching share of the time instead, so the
      // speed the eye sees does not change. `translateTo` rather than
      // `slideNext`: the pause left the swiper between two snap points, and
      // only the first of the two moves from where the strip actually is.
      if (next === undefined || !step) {
        swiper.autoplay.start();
        return;
      }

      this.resumed = () => {
        this.resumed = null;
        if (this.paused || !this.swiper) return;
        this.swiper.animating = false;
        this.swiper.updateActiveIndex();
        this.swiper.updateSlidesClasses();
        this.swiper.autoplay.start();
      };

      swiper.once('transitionEnd', this.resumed);
      swiper.setTransition(0);
      swiper.translateTo(-next, Math.max(120, Math.round(this.speed * ((next - from) / step))));
    }
  }

  customElements.define('plantica-press', PlanticaPress);
})();
